import { type Channel } from '@commercetools/platform-sdk'
import {
	type LoaderFunction,
	type LoaderFunctionArgs,
	redirectDocument,
	type MetaFunction,
	json,
} from '@remix-run/node'

import { promiseHash } from 'remix-utils/promise'
import { type Category, type CategoryData } from '#app/constants/clerk-api'
import {
	getUserPreferences,
	getUserPreferencesAndHeader,
} from '#app/cookies/user-preferences.server'
import { getVisitor } from '#app/cookies/visitor.server'
import ProductListItemsClerk from '#app/features/plp-clerk/product-list-items-clerk'
import {
	generateApiUrls,
	getFacets,
	getProducts,
	getProductsOnSale,
} from '#app/server/clerk/plp.server'
import { getStores } from '#app/server/commerce-tools/store/store.server'
import { languageSession } from '#app/utils/localisation/lang.server'
import { getLanguageFromCookieOrBrowserOrFallback } from '#app/utils/localisation/lokalisation-utilities'
import { combineHeaders } from '#app/utils/misc'

/**
 * Loader function to fetch and prepare data for the product page.
 *
 * @param {Object} context - The context object containing request and params.
 * @param {Request} context.request - The request object.
 * @param {Object} context.params - The parameters object containing route parameters.
 * @param {string} context.params.slug - The slug parameter from the URL.
 *
 * @returns {Promise<Response | Object>} - Returns a redirectDocument response or a deferred data object.
 *
 * @see {@link https://www.figma.com/board/0pT2MBWgdLGKKfXBAMcVZx/Dev-documentation?node-id=282-471&t=oTj06Q9PQX3DSvie-4 | FlowDiagram for this loader and the nudge single orders}
 *
 */
export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	const { slug } = params
	if (!slug) return null

	const visitorId = await getVisitor(request)
	const url = new URL(request.url)
	const currentUrl = url.toString()

	const userPreferences = await getUserPreferences(request)
	let searchParams = new URLSearchParams(url.search)

	const isEmpty = searchParams.get('empty-category') === 'true'
	const categoryKey = slug
	const categoryMainRoot = categoryKey.split('-')[0]
	const isKids = categoryKey.includes('kids')

	const page = parseInt(searchParams.get('p') || '1', 10)
	const cookieHeader = request.headers.get('Cookie')
	const session = await languageSession.getSession(cookieHeader)
	let storeParams = searchParams.get('store')?.split(',') || []

	// handling the case when the user has selected stores in the cookie but the store param is empty
	if (
		userPreferences?.selectedStores?.length > 0 &&
		storeParams.length === 0 &&
		!isEmpty
	) {
		const stores = await getStores()
		const storeKeys = userPreferences.selectedStores
			.map(
				(selectedStore: { key: string }) =>
					stores.find(
						store =>
							store.distributionChannels[0].obj?.key === selectedStore.key,
					)?.key || '',
			)
			.filter((storeKey: string) => storeKey !== '')

		if (storeKeys.length > 0) {
			searchParams.set('store', storeKeys.join(','))
		}

		return redirectDocument(`/products/${slug}?${searchParams.toString()}`)
	} else if (
		// handling the case when the user has no selected stores in the cookie but the store param is not empty
		(!userPreferences?.selectedStores ||
			userPreferences?.selectedStores?.length === 0) &&
		storeParams.length > 0 &&
		!isEmpty
	) {
		const stores = await getStores()

		const storesFromURL = stores
			.filter(sc => storeParams.includes(sc?.key || ''))
			.map(sc => ({
				key: sc?.distributionChannels[0].obj?.key,
				name: sc?.distributionChannels[0].obj?.name?.en,
			}))

		const { headers } = await getUserPreferencesAndHeader(request, {
			selectedStores: storesFromURL,
		})

		return redirectDocument(`/products/${slug}?${searchParams.toString()}`, {
			headers: combineHeaders(headers),
		})
	}

	if (isEmpty) {
		searchParams.delete('store')
	}
	const currentLanguage = session.get('sessionLanguage') || 'da'
	const language = getLanguageFromCookieOrBrowserOrFallback(
		currentLanguage,
		request,
	)
	const apiURLs = generateApiUrls(
		searchParams,
		categoryKey,
		language,
		visitorId,
	)

	const { productsData, onSaleAvailable, categoriesResponse, facets, stores } =
		await promiseHash({
			productsData: getProducts(apiURLs.productsAPI),
			onSaleAvailable: getProductsOnSale(categoryKey, visitorId),
			categoriesResponse: fetch(apiURLs.categoriesAPI, {
				headers: {
					Accept: 'application/json',
				},
			}),
			facets: getFacets(
				searchParams,
				categoryKey,
				isKids,
				currentLanguage,
				visitorId,
			),
			stores: getStores(),
		})
	const storeChannels = stores.map(store => store.distributionChannels[0].obj)

	const filteredByGenderStoreChannels = storeChannels.filter(
		sc => sc?.custom?.fields?.genderStoreType?.includes(categoryMainRoot),
	)
	// handle case after fetching products and facets
	if (
		!isEmpty &&
		facets.totalCount === 0 &&
		userPreferences?.selectedStores &&
		userPreferences?.selectedStores.length > 0
	) {
		// check if the all the selected stores gender match the current category

		const storeKeys = userPreferences.selectedStores.reduce(
			(acc: string[][], { key }: { key: string }) => {
				const genderType =
					storeChannels.find(store => store?.key === key)?.custom?.fields
						.genderStoreType || ''
				if (genderType.includes(categoryMainRoot)) {
					acc.push(genderType)
				}
				return acc
			},
			[],
		)

		const { headers } = await getUserPreferencesAndHeader(request, {
			selectedStores: [],
		})

		if (storeKeys.length > 0) {
			// we hit the correct category but there is products in this store
			searchParams.set('empty-category', 'true')
			return redirectDocument(`/products/${slug}?${searchParams.toString()}`)
		} else {
			// we handle the case when we have a selected store but hit another gender so we just remove the store and go back to the main category
			searchParams.delete('store')

			return redirectDocument(`/products/${slug}?${searchParams.toString()}`, {
				headers: combineHeaders(headers),
			})
		}
	}

	if (!categoriesResponse.ok) {
		throw new Error('Failed to fetch categories')
	}
	const categoriesData = (await categoriesResponse.json()) as CategoryData

	return json({
		currentUrl,
		storesChannels: filteredByGenderStoreChannels as Channel[],
		productsData,
		categoriesData,
		page,
		facetsData: { facets: facets.facets },
		totalCount: facets.totalCount,
		totalCountAllStores: facets.facets.totalCountAllStores,
		locale: language,
		onSaleAvailable,
	})
}

export default function PLP() {
	return (
		<div>
			<ProductListItemsClerk />
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ params, data }) => {
	const { slug } = params
	const { categoriesData } = data
	const canonicalUrl = new URL(data.currentUrl)
	canonicalUrl.hostname = canonicalUrl.hostname.replace('www.', '')
	canonicalUrl.search = ''

	const category = categoriesData.result.find(
		(category: Category) => category.id === slug,
	) as Category

	const title = category?.titleSEO || category.name

	const description = category?.descriptionSEO || category.name

	return [
		{
			title: title,
		},
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl.toString() },

		{
			description: description,
		},
		{
			og_title: title,
		},
		{
			og_description: description,
		},
		{
			twitter_title: title,
		},
		{
			twitter_description: description,
		},
	]
}
